import { Controller } from "@hotwired/stimulus"

// The account's parts one at a time, as GitHub's and Shopify's settings show one page at a time (accounts/show): a
// link in the list (beside the parts on a wide screen, a row of tabs above them on a phone) swaps in its part, and
// every part shows from the top of the page, the account's name above it, wherever it was scrolled to. The part shown
// is the link's, kept in the address (#devices), or the one holding what the address names (#delete, the
// page sent back to it). A part's own name in the address doesn't jump the page down to it: the part keeps its name as
// data-part, not as an id. Without this, every part is on the page and each name jumps to its part.
export default class extends Controller {
  static targets = ["link", "part"]

  connect() {
    this.partTargets.forEach((p) => {
      p.dataset.part ||= p.id
      p.removeAttribute("id")
    })
    this.fit = () => this.show(this.fromHash())
    window.addEventListener("hashchange", this.fit)
    this.element.classList.add("is-paged")
    this.fit()
    // Come in by a part's name (/account#kept), the page may already have jumped down to it.
    // A fresh load jumps again once it's loaded.
    if (this.partTargets.some((p) => `#${p.dataset.part}` === location.hash)) {
      this.toTop()
      if (document.readyState !== "complete") window.addEventListener("load", () => this.toTop(), { once: true })
    }
  }

  disconnect() {
    window.removeEventListener("hashchange", this.fit)
  }

  pick(event) {
    event.preventDefault()
    const hash = event.currentTarget.hash
    history.replaceState(history.state, "", hash)
    this.show(this.fromHash())
    this.toTop()
  }

  toTop() {
    window.scrollTo({ top: 0, behavior: "instant" })
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "instant" }))
  }

  fromHash() {
    const name = location.hash.slice(1)
    const named = name && document.getElementById(name)
    const part = this.partTargets.find((p) => p.dataset.part === name || (named && p.contains(named)))
    return (part ?? this.partTargets[0]).dataset.part
  }

  show(id) {
    this.partTargets.forEach((p) => p.classList.toggle("is-on", p.dataset.part === id))
    this.linkTargets.forEach((a) => (a.hash === `#${id}` ? a.setAttribute("aria-current", "true") : a.removeAttribute("aria-current")))
    // On a phone's row of tabs, the one picked comes into view.
    const on = this.linkTargets.find((a) => a.hash === `#${id}`), row = on?.parentElement
    if (row && row.scrollWidth > row.clientWidth) row.scrollLeft = on.offsetLeft - (row.clientWidth - on.offsetWidth) / 2
  }
}
